import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Outlet, Link, useLocation, useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { PlanBadge, JG12Logo } from './ClientDashboard';
import LimiteDeError from '../components/LimiteDeError';
import { CampanaDelEquipo, PeticionesDeCompra } from '../components/AvisosDelEquipo';
import { useCampanaDelEquipo } from '../hooks/useCampanaDelEquipo';
import { prettyToken } from '../lib/labels';
import { verComo } from '../lib/modoRevision';
import { estadoClienteLabel, estadoDeAcceso, plural } from '../lib/labels';
import { esDeLaLista } from '../lib/filtrosDeClientes';
import { euros, eurosOHueco, eurosRedondos } from '../lib/precios';
import { PAGO_EN_TRAMOS, pagaEnTramos, seRenuevaSolo } from '../lib/loQueFaltaEnElAlta';
import { planesDelEnlaceDePago } from '../lib/planesParaElegir';
import {
    LayoutDashboard, Users, CreditCard, Dumbbell,
    MessageCircle, LogOut,
    ChevronRight, DollarSign, FileText,
    AlertTriangle, UserCheck, UserMinus, UserPlus, Utensils, Apple, Layers,
    Menu, X, Phone, Receipt, ClipboardList, CalendarClock, CalendarRange, Clock, Gauge, SlidersHorizontal, ShieldCheck,
    BookOpen,
} from 'lucide-react';
import { mensajeDeError } from '../lib/mensajeDeError';
import { diaDeCalendario, diasDesdeHoy, pintarDia } from '../lib/horaEspana';

import { entradasDelMenu, seAvisanLosLeads, seEnsenanLasLlamadas } from '../lib/pantallasDelPanel';
// Colores para el desglose por plan (cualquier plan sin color cae en el gris).
// UN COLOR POR PLAN, Y NINGUNO SIN COLOR. Faltaban los cuatro que se venden hoy y los que
// llegaron el 17-08 desde Calma (Reto 12en12, Plan Personalizado, Básica): salían todos en
// el mismo gris de «desconocido», y en un reparto por colores dos planes del mismo gris son
// un plan. `calma` (sin normalizar) comparte el de CALMA 12 porque son el mismo.
const PLAN_COLORS = {
    nivel1: '#F59E0B', nivel2: '#FF671F', nivel3: '#DC2626', membresia: '#0EA5E9',
    plan_12en12: '#DC2626', plan_essential: '#FF671F', plan_calculadora: '#F59E0B',
    reto12en12_gold: '#EAB308', gold: '#EAB308',
    reto12en12_silver: '#9CA3AF', silver: '#9CA3AF',
    bronze: '#C2410C', elm: '#FF671F', reto60: '#22C55E',
    calculadora_jp: '#3B82F6', mantenimiento: '#8B5CF6',
    premium: '#EC4899', plan_6m: '#14B8A6', sin_plan: '#555555',
    reto12en12: '#A16207', personalizado: '#7C3AED', basica: '#64748B',
    calma12: '#06B6D4', CalMa: '#06B6D4', calma: '#06B6D4',
};

// Panel "Por hacer esta semana": tres columnas accionables (sin macros / sin rutina /
// reporte pendiente), con filtro por clientes al corriente de pago (tarea 19).
const TodoSemana = ({ todo, soloAlCorriente, setSoloAlCorriente, navigate, forzarRutina }) => {
    // Qué columnas plegadas se han abierto con su «verlos» (punto 134, ver abajo).
    const [desplegadas, setDesplegadas] = useState({});
    if (!todo) return null;
    const flt = (arr) => (arr || []).filter(c => !soloAlCorriente || c.al_corriente);
    // «SIN RUTINA» NO ES UNA LISTA DE PENDIENTES SI ESTÁN TODOS.
    //
    // Con 227 de 228 en rojo, esa columna no ayuda a priorizar: no es trabajo pendiente, es el
    // estado de la casa, y ocupa un cuarto de la mejor herramienta del panel. Jesús, 11-08:
    // «si es verdad, no es un aviso: es el estado normal, y conviene sacarlo de la lista de
    // pendientes hasta que se empiecen a poner rutinas».
    // Vuelve sola en cuanto deje de serlo: el criterio es que falten menos de nueve de cada
    // diez. Y no se esconde nada, porque la pantalla de Rutinas la lista entera.
    const conRutinaEnPlan = Number(todo.con_rutina_en_plan || 0);
    const sinRutina = todo.sin_rutina || [];
    // `forzarRutina` es el modo revisión (`/admin?ver=sin_rutina`, solo equipo): en dev le
    // falta a 154 de 156 y la columna se esconde sola, así que sin esto no había forma de
    // mirar cómo queda la caja (4-09). No toca ningún dato.
    const rutinaEsLoNormal = !forzarRutina && conRutinaEnPlan > 0 && sinRutina.length >= conRutinaEnPlan * 0.9;

    const cols = [
        { key: 'macros', label: 'Sin macros', icon: Apple, color: '#FF671F', items: flt(todo.sin_macros) },
        ...(rutinaEsLoNormal ? [] : [
            /* «SIN PONER» Y NO «SIN UNA ACTIVA» (puntos 67 y 69, 28-08). Desde hoy esta
               columna cuenta también la rutina entregada en PDF, igual que la pantalla de
               Rutinas, así que «sin una activa» describía la cuenta vieja: al que tiene su
               PDF no le falta rutina, le falta estar en la tabla de rutinas estructuradas,
               que es otra cosa y no es trabajo pendiente de nadie. */
            /* Y DESDE EL 4-09 ES EL MISMO NÚMERO QUE EN RUTINAS (punto 103 del artefacto «La
               app, pantalla por pantalla»: «el Inicio del panel sigue diciendo Sin rutina: 93
               como si no tuviera ninguno»): la lista sale de la misma función del backend
               (`core.rutina_puesta`), con las mismas personas y el mismo criterio de qué plan
               la lleva. Y va PLEGADA: el número, y los nombres detrás de un «verlos»
               (punto 134), que ciento veinte nombres seguidos no se leen, se cuentan. */
            { key: 'rutina', label: 'Sin rutina', icon: Dumbbell, color: '#3B82F6', items: flt(sinRutina), plegada: true },
        ]),
        { key: 'reportes', label: 'Reporte pendiente', icon: FileText, color: '#EAB308', items: flt(todo.reporte_pendiente) },
        // «No puedo esta semana»: avisaron con el botón, así que no ensucian la lista de
        // los que faltan (doc 19-08). Con lo que hayan escrito, y en rojo el que ya lleva
        // dos seguidos (ese además dispara la tarea automática de contactarle).
        ...((todo.reporte_aplazado || []).length ? [
            { key: 'aplazados', label: 'Aplazados a la semana que viene', icon: CalendarClock, color: '#22C55E', items: flt(todo.reporte_aplazado) },
        ] : []),
        // «Sin contacto» NO SE PINTA (doc 21-08). El dato solo cuenta el chat interno y
        // el webhook de GHL: ni el feedback de reportes ni los ajustes de macros cuentan
        // como contacto, así que «177 · nunca» era falso. Fuera hasta que el contacto se
        // pueda registrar de verdad; el backend sigue mandando `sin_contacto` por si acaso.
    ];
    return (
        <Card className="bg-[#111111] border-[#222]" data-testid="todo-semana">
            <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                    <p className="text-xs font-bold text-white/40 uppercase tracking-wider">Por hacer esta semana</p>
                    <label className="flex items-center gap-2 text-xs text-white/50 cursor-pointer select-none">
                        <input type="checkbox" checked={soloAlCorriente} onChange={e => setSoloAlCorriente(e.target.checked)}
                            className="accent-[#FF671F]" data-testid="todo-filter-alcorriente" />
                        Solo al corriente de pago
                    </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    {cols.map(col => (
                        <div key={col.key} className="bg-[#0A0A0A] rounded-xl border border-[#222] p-3">
                            <div className="flex items-center gap-2 mb-1">
                                <col.icon className="w-4 h-4" style={{ color: col.color }} />
                                <span className="text-sm font-bold text-white">{col.label}</span>
                                <span className="ml-auto text-sm font-bold" style={{ color: col.color }}>{col.items.length}</span>
                            </div>
                            {/* La plegada enseña solo el número hasta que se pide verlos (punto 134). */}
                            {col.plegada && col.items.length > 0 && !desplegadas[col.key] ? (
                                <button onClick={() => setDesplegadas(d => ({ ...d, [col.key]: true }))}
                                    data-testid={`todo-ver-${col.key}`}
                                    className="text-[#FF671F] text-xs hover:underline py-1">verlos</button>
                            ) : (
                            <div className="space-y-0.5 max-h-64 overflow-y-auto">
                                {col.plegada && col.items.length > 0 && (
                                    <button onClick={() => setDesplegadas(d => ({ ...d, [col.key]: false }))}
                                        data-testid={`todo-ocultar-${col.key}`}
                                        className="text-white/40 text-xs hover:underline py-1">ocultar</button>
                                )}
                                {col.items.length === 0 && <p className="text-white/25 text-xs py-3 text-center">Nada pendiente</p>}
                                {col.items.map(c => (
                                    <button key={c.client_id} onClick={() => navigate(`/admin/clients/${c.client_id}`)}
                                        className="w-full px-2 py-1.5 rounded-lg hover:bg-white/5 text-left">
                                        <span className="flex items-center gap-2 w-full">
                                        <span className="flex-1 min-w-0 truncate text-sm text-white/80">{c.name}</span>
                                        {col.key === 'reportes' && c.overdue && <span className="text-[9px] text-red-400 font-bold uppercase tracking-wide">tarde</span>}
                                        {col.key === 'aplazados' && c.seguidos >= 2 && <span className="text-[9px] text-red-400 font-bold uppercase tracking-wide">{c.seguidos} seguidos</span>}
                                        {!c.al_corriente && <span title="Pago pendiente" className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />}
                                        <ChevronRight className="w-3.5 h-3.5 text-white/20 flex-shrink-0" />
                                        </span>
                                        {/* Lo que escribió al aplazar («¿Quieres decirme algo?»), debajo del nombre. */}
                                        {col.key === 'aplazados' && c.nota && (
                                            <span className="block text-[11px] text-white/40 italic truncate" title={c.nota}>«{c.nota}»</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                            )}
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
};

// "Esta semana te tocan estos seis" (punto 29 del doc del 07-08). Es la pregunta que Jesús
// se hace todos los lunes y que hasta ahora resolvía mirando una hoja de cálculo aparte:
// quién lleva más tiempo sin que le muevan los macros. Arriba, el que más.
const CUANTOS_TE_TOCAN = 6;

const EstaSemanaTeTocan = ({ items, navigate }) => {
    if (!items || items.length === 0) return null;
    const seis = items.slice(0, CUANTOS_TE_TOCAN);
    const _dias = (c) => c.nunca_ajustado ? 'nunca' : `${c.dias_sin_ajuste} d`;
    return (
        <Card className="bg-[#111111] border-[#FF671F]/25" data-testid="te-tocan">
            <CardContent className="p-5">
                <div className="flex items-center justify-between mb-1 gap-3 flex-wrap">
                    <p className="text-xs font-bold text-white/40 uppercase tracking-wider">
                        {seis.length === 1 ? 'Esta semana te toca este' : `Esta semana te tocan estos ${seis.length}`}
                    </p>
                    <button onClick={() => navigate('/admin/clients?orden=sin_tocar')}
                        className="text-[11px] text-[#FF671F] hover:underline" data-testid="te-tocan-todos">
                        {items.length === 1 ? 'Verlo en la lista' : `Ver los ${items.length} ordenados`}
                    </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
                    {seis.map(c => (
                        <button key={c.client_id} onClick={() => navigate(`/admin/clients/${c.client_id}`)}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0A0A0A] border border-[#222] hover:border-[#FF671F]/40 text-left">
                            <span className="flex-1 min-w-0 truncate text-sm text-white/80">{c.name}</span>
                            {/* Los días desde el último ajuste, y desde el mes en naranja: no
                                es un umbral del servidor, es lo que salta a la vista. */}
                            <span className={`text-[11px] font-bold tabular-nums ${c.nunca_ajustado || (c.dias_sin_ajuste || 0) >= 30 ? 'text-[#FF671F]' : 'text-white/40'}`}>
                                {_dias(c)}
                            </span>
                            {!c.al_corriente && <span title="Pago pendiente" className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />}
                            <ChevronRight className="w-3.5 h-3.5 text-white/20 flex-shrink-0" />
                        </button>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
};

// Alerta: quien ha elegido el Nivel 3 en el test y espera que le llamen. Solo aparece
// cuando hay alguna: un aviso que está siempre deja de ser un aviso. Va arriba del todo
// porque es lo único del panel donde hay alguien esperando al otro lado del teléfono.
// LOS TRES SE VENDEN POR LLAMADA (artefacto «Los tres planes», 24-09): después de hablar
// se elige cuál se cobra. Viene puesto el que pidió, y si no pidió ninguno el 12EN12, que
// es la puerta de entrada.
// QUÉ PLANES Y A QUÉ PRECIO sale del catálogo (`lib/planesParaElegir.planesDelEnlaceDePago`,
// 12-10): la lista estaba escrita aquí a mano con los importes dentro.
const LlamadasPendientes = ({ llamadas, onAtendida, onCobrar, onVerFicha, onPonerPago, onPagoResuelto, generandoEnlace, esAdmin, planCatalog }) => {
    const [planes, setPlanes] = useState({});
    // «Se renueva solo» por lead (16-10): el enlace es de suscripción y Stripe cobra cada ciclo.
    const [renueva, setRenueva] = useState({});
    // «Pago 4 semanas / Pago 12 semanas» por lead (Francisco, 16-10): solo en los planes que lo
    // admiten (`/plans`, `pago_en_tramos`), y el de 4 siempre se renueva solo.
    const [pago, setPago] = useState({});
    const enTramos = (l, plan) => pagaEnTramos(planCatalog, plan, pago[l.id]);
    const cobrar = (l, plan) => onCobrar(l, plan,
        seRenuevaSolo({ recurrente: renueva[l.id], pagoSemanas: enTramos(l, plan) ? PAGO_EN_TRAMOS : '12' }),
        enTramos(l, plan) ? Number(PAGO_EN_TRAMOS) : undefined);
    const casillaRenueva = (l, plan) => {
        const tramo = planCatalog?.[plan]?.pago_en_tramos;
        return (
            <>
                {tramo && (
                    <select value={enTramos(l, plan) ? PAGO_EN_TRAMOS : '12'} data-testid={`pago-semanas-${l.id}`}
                        onChange={(e) => setPago(p => ({ ...p, [l.id]: e.target.value }))}
                        className="bg-[#111] border border-[#333] text-white text-xs rounded-lg px-2 py-1.5">
                        <option value="12">Pago 12 semanas</option>
                        {/* Sin importe: en la renovación por llamada se cobra el tercio de SU precio
                            congelado, no el del catálogo; el aviso del enlace dice el de verdad. */}
                        <option value={PAGO_EN_TRAMOS}>Pago 4 semanas · un tercio cada 4 semanas</option>
                    </select>
                )}
                {enTramos(l, plan) ? (
                    <span className="text-xs text-white/70">Se renueva solo cada 4 semanas</span>
                ) : (
                    <label className="flex items-center gap-1.5 text-xs text-white/70" title="Stripe le cobra solo cada ciclo">
                        <input type="checkbox" checked={!!renueva[l.id]} data-testid={`renueva-solo-${l.id}`}
                            onChange={(e) => setRenueva(r => ({ ...r, [l.id]: e.target.checked }))} />
                        Se renueva solo
                    </label>
                )}
            </>
        );
    };
    const delEnlace = (l) => planesDelEnlaceDePago(planCatalog, l);
    if (!llamadas || llamadas.length === 0) return null;
    return (
        <Card className="bg-[#FF671F]/10 border-[#FF671F]/40" data-testid="llamadas-pendientes">
            <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                    <Phone className="w-4 h-4 text-[#FF671F]" />
                    <span className="text-sm font-bold text-white uppercase tracking-wider">
                        Piden que les llamemos
                    </span>
                    <Badge className="bg-[#FF671F] text-white border-0 text-xs">{llamadas.length}</Badge>
                </div>
                <div className="space-y-2">
                    {llamadas.map(l => (
                        <div key={l.id} data-testid={`llamada-${l.id}`}
                            className="bg-[#0A0A0A] rounded-xl border border-[#222] p-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                            <div className="min-w-0">
                                <p className="text-sm font-bold text-white truncate">{l.name}</p>
                                <p className="text-[11px] text-white/40 truncate">{l.email}</p>
                            </div>
                            {/* El teléfono es el dato accionable: grande y pulsable para llamar. */}
                            <a href={`tel:${(l.phone || '').replace(/\s/g, '')}`}
                                className="text-base font-bold text-[#FF671F] hover:underline tabular-nums">
                                {l.phone || 'sin teléfono'}
                            </a>
                            {/* Para qué plan y a qué hora pidió la llamada (ALT-04, 14-10). */}
                            {(l.llamada_pedida?.plan || l.llamada_pedida?.franja) && (
                                <span className="text-[11px] text-white/60" data-testid={`llamada-pedida-${l.id}`}>
                                    {[l.llamada_pedida.plan, l.llamada_pedida.franja].filter(Boolean).join(' · ')}
                                </span>
                            )}
                            {l.pide_contacto?.origen === 'que_me_escriban' && (
                                <span className="text-[11px] text-white/60">Pide que le escriban</span>
                            )}
                            {/* El plan por llamada pedido desde la pantalla de renovación (12-10). */}
                            {l.pide_contacto?.origen === 'renovacion_llamada' && (
                                <span className="text-[11px] text-white/60">Quiere renovar por llamada</span>
                            )}
                            {l.dias_esperando > 0 && (
                                <span className={`text-[10px] font-bold uppercase tracking-wide ${
                                    l.dias_esperando >= 2 ? 'text-red-400' : 'text-white/40'}`}>
                                    {l.dias_esperando === 1 ? 'de ayer' : `hace ${l.dias_esperando} días`}
                                </span>
                            )}
                            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                                {/* QUIÉN ES, DE UN VISTAZO (09-10). El enlace de pago solo se le
                                    niega a quien tiene el plan ACTIVO: a ese se le renueva o se
                                    le cambia desde su ficha. Quien tiene cuenta y no tiene plan
                                    (nunca pagó o caducó) es justo quien quiere contratar, y al
                                    pagar el plan se le pone a su cuenta. Lo decide el servidor
                                    (`plan_activo`) y también lo rechaza si se le pide. */}
                                {l.ya_es_cliente && (
                                    <>
                                        <Badge className="bg-white/10 text-white/80 border-0 text-[11px]"
                                            data-testid={`ya-es-cliente-${l.id}`}>
                                            {/* «Sin plan» solo a quien no tiene ninguno: el que lo tiene y está
                                                inactivo, caducado o sin pagar no tiene ACCESO, que es otra
                                                cosa (`sin_acceso_por`, las palabras del candado). */}
                                            {l.plan_activo ? 'Con plan activo'
                                                : (l.sin_acceso_por && l.sin_acceso_por !== 'sin_plan'
                                                    ? 'Tiene cuenta, sin acceso' : 'Tiene cuenta, sin plan')}
                                        </Badge>
                                        {l.cliente_id && (
                                            <Button size="sm" variant="ghost" onClick={() => onVerFicha(l)}
                                                data-testid={`ver-ficha-${l.id}`}
                                                className="text-xs text-[#FF671F] hover:text-[#FF671F]">
                                                Ver su ficha
                                            </Button>
                                        )}
                                    </>
                                )}
                                {/* PAGÓ Y NO TIENE EL PLAN PUESTO (10-10): pagó sin cuenta y se
                                    registró después, o tenía ya un plan activo, o su cuenta
                                    estaba de baja. No se le ofrece OTRO enlace: se le pone el
                                    plan que ya pagó, o se marca como resuelto si se arregló a
                                    mano (alta, cambio de plan o devolución). */}
                                {l.pago_pendiente && (
                                    <>
                                        <Badge className="bg-red-500/15 text-red-300 border-0 text-[11px]"
                                            data-testid={`pago-pendiente-${l.id}`}>
                                            Pagó {euros(l.pago_pendiente.importe_eur)} y no tiene el plan puesto
                                        </Badge>
                                        {/* Los dos botones son de Cobros: solo los ve un administrador
                                            (el servidor tampoco se lo deja a un entrenador). */}
                                        {esAdmin && l.ya_es_cliente && !l.plan_activo && (
                                            <Button size="sm" onClick={() => onPonerPago(l)}
                                                data-testid={`poner-pago-${l.id}`}
                                                className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white text-xs">
                                                Ponerle el plan pagado
                                            </Button>
                                        )}
                                        {esAdmin && (
                                            <Button size="sm" variant="ghost" onClick={() => onPagoResuelto(l)}
                                                data-testid={`pago-resuelto-${l.id}`}
                                                className="text-xs text-white/60 hover:text-white">
                                                Ya está resuelto
                                            </Button>
                                        )}
                                    </>
                                )}
                                {/* El enlace de pago es de Cobros: solo lo saca un administrador
                                    (12-10; el servidor tampoco se lo deja a un entrenador). */}
                                {esAdmin && !l.plan_activo && !l.pago_pendiente && (
                                    <>
                                        {/* Ya hablado: se elige el plan y se le cobra con tarjeta. */}
                                        <select value={planes[l.id] || delEnlace(l).porDefecto}
                                            onChange={(e) => setPlanes(p => ({ ...p, [l.id]: e.target.value }))}
                                            data-testid={`plan-enlace-${l.id}`}
                                            className="bg-[#111] border border-[#333] text-white text-xs rounded-lg px-2 py-1.5">
                                            {delEnlace(l).opciones.map(p => <option key={p.code} value={p.code}>{p.label}</option>)}
                                        </select>
                                        {casillaRenueva(l, planes[l.id] || delEnlace(l).porDefecto)}
                                        <Button size="sm" onClick={() => cobrar(l, planes[l.id] || delEnlace(l).porDefecto)} disabled={generandoEnlace === l.id}
                                            data-testid={`enlace-pago-${l.id}`}
                                            className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white text-xs disabled:opacity-60">
                                            {generandoEnlace === l.id ? 'Creando...' : 'Enlace de pago'}
                                        </Button>
                                    </>
                                )}
                                {/* LA RENOVACIÓN POR LLAMADA (12-10): con el plan activo no hay
                                    enlace, salvo el del plan que pidió renovar por llamada, que no
                                    tiene otra forma de cobrarse. El servidor dice cuál es
                                    (`renueva_por_llamada`) y solo acepta ese. */}
                                {esAdmin && l.plan_activo && l.renueva_por_llamada && !l.pago_pendiente && casillaRenueva(l, l.renueva_por_llamada.plan)}
                                {esAdmin && l.plan_activo && l.renueva_por_llamada && !l.pago_pendiente && (
                                    <Button size="sm" onClick={() => cobrar(l, l.renueva_por_llamada.plan)}
                                        disabled={generandoEnlace === l.id}
                                        data-testid={`enlace-renovacion-${l.id}`}
                                        className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white text-xs disabled:opacity-60">
                                        {generandoEnlace === l.id ? 'Creando...'
                                            : `Enlace de pago · ${l.renueva_por_llamada.nombre || l.renueva_por_llamada.plan}`}
                                    </Button>
                                )}
                                {l.pide_que_le_llamen !== false && (
                                    <Button size="sm" variant="ghost" onClick={() => onAtendida(l)}
                                        data-testid={`llamada-atendida-${l.id}`}
                                        className="text-xs text-white/60 hover:text-white">
                                        Ya le he llamado
                                    </Button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
};

// EL DINERO, DE UN SOLO SITIO (Jesús, 11-09, punto 24). Aquí había dos formateadores
// propios y en Paneles un tercero, y por eso en la misma pantalla convivían «17.981 €» y
// «1500€». Ahora los tres salen de `lib/precios`, que es donde ya se escribía el precio de
// los planes, y tienen su test. Si el formato vuelve a cambiar, cambia en un sitio.

// LA FECHA, EN ESPAÑOL (Jesús, 11-09, punto 25). La cabecera del bloque de renovaciones
// ponía «Del 2026-09-11 al 2026-09-18», que es la fecha como la guarda la base de datos.
const delAl = (desde, hasta) => {
    const d = new Date(`${desde}T12:00:00`), h = new Date(`${hasta}T12:00:00`);
    if (isNaN(d) || isNaN(h)) return `Del ${desde} al ${hasta}`;
    const mes = (x) => x.toLocaleDateString('es-ES', { month: 'long' });
    // Dentro del mismo mes se dice una vez: «Del 11 al 18 de septiembre».
    return mes(d) === mes(h)
        ? `Del ${d.getDate()} al ${h.getDate()} de ${mes(h)}`
        : `Del ${d.getDate()} de ${mes(d)} al ${h.getDate()} de ${mes(h)}`;
};

// Una entrada del menú del panel (barra lateral, cajón móvil y barra de abajo). Si viene con
// `disabled` se pinta igual pero apagada y sin enlace: se ve, pero no se puede abrir.
const EntradaDelMenu = ({ item, className, onClick, children }) => {
    if (item.disabled) {
        const apagada = className
            .split(' ')
            .filter((c) => !c.startsWith('hover:') && !c.startsWith('text-'))
            .join(' ');
        return (
            <span aria-disabled="true" title="No disponible de momento"
                data-testid={`menu-deshabilitado-${item.label.toLowerCase()}`}
                className={`${apagada} text-white/25 cursor-not-allowed select-none`}>
                {children}
            </span>
        );
    }
    return <Link to={item.path} onClick={onClick} className={className}>{children}</Link>;
};

// Admin Dashboard Home
const AdminDashboard = () => {
    const { api, planCatalog, user } = useAuth();
    const navigate = useNavigate();
    // EL DINERO, SOLO AL ADMIN (P62, doc 23-08). El entrenador también entra en esta
    // portada, y aquí le salían el MRR, los próximos cobros con importes y los avisos de
    // pagos. La regla ya regía en las secciones; ahora también aquí, y el backend además
    // ha dejado de mandarle los importes (no es solo esconderlos).
    const esAdmin = user?.role === 'admin';
    const [stats, setStats] = useState(null);
    const [upcoming, setUpcoming] = useState([]);
    // «A quién acaba la semana que viene la suscripción», que es lo que Jesús le pregunta a
    // Jenny todos los días (reunión del 4-09). Los próximos cobros de más abajo contestan la
    // pregunta contraria: los que siguen.
    const [semana, setSemana] = useState(null);
    const [verSemana, setVerSemana] = useState('se_acaban');
    const [clients, setClients] = useState([]);
    const [cadence, setCadence] = useState([]);
    const [loading, setLoading] = useState(true);
    // Motor de macros v2: dietas reportadas que no cuadran, pendientes de revisar.
    const [revisiones, setRevisiones] = useState([]);
    // "Por hacer esta semana": sin macros / sin rutina / reporte pendiente (tarea 19).
    const [todo, setTodo] = useState(null);
    const [soloAlCorriente, setSoloAlCorriente] = useState(false);
    // Nivel 3: piden llamada desde el test de nivel y esperan a que alguien marque.
    const [llamadas, setLlamadas] = useState([]);
    // Avisos del sistema: cobros fallidos y bajas automáticas (los de reporte vencido ya no
    // se crean, 17-10). Se venían
    // guardando desde marzo y NO SE ENSEÑABAN EN NINGUNA PANTALLA (14-08-2026): el endpoint
    // existía, el panel no lo pedía, y lo único que veía el equipo de un cobro fallido era
    // que el cliente dejaba de estar activo.
    const [avisos, setAvisos] = useState([]);

    const marcarLlamadaAtendida = async (l) => {
        try {
            await api.post(`/leads/${l.id}/llamada-atendida`);
            setLlamadas(prev => prev.filter(x => x.id !== l.id));
            // Al que ya es cliente no se le mueve el lead: solo sale de esta lista.
            toast.success(l.ya_es_cliente ? `Apuntado: ya hablaste con ${l.name}`
                : `${l.name} queda como contactado`);
        } catch {
            toast.error('No se pudo marcar la llamada');
        }
    };

    // El pago de un enlace que se quedó sin poner (`core/cobro_de_lead.py`): ponérselo a su
    // cuenta, o cerrarlo si el equipo ya lo resolvió a mano. En los dos casos la fila se va
    // si no queda nada más pendiente; se vuelve a pedir la lista para no adivinarlo aquí.
    const recargarLlamadas = () => api.get('/leads/llamadas-pendientes')
        .then(r => setLlamadas(r.data?.llamadas || [])).catch(() => {});
    const ponerPagoPendiente = async (l) => {
        try {
            await api.post(`/leads/${l.id}/poner-pago`);
            toast.success(`${l.name || l.email} ya tiene puesto el plan que pagó`);
            recargarLlamadas();
        } catch (e) {
            toast.error(mensajeDeError(e, 'No se pudo poner el plan'));
        }
    };
    const marcarPagoResuelto = async (l) => {
        try {
            await api.post(`/leads/${l.id}/pago-resuelto`);
            toast.success(`El pago de ${l.name || l.email} queda como resuelto`);
            recargarLlamadas();
        } catch (e) {
            toast.error(mensajeDeError(e, 'No se pudo marcar el pago'));
        }
    };

    // Cobro del Nivel 3 despues de la llamada (doc 03-08): se genera el enlace de pago
    // con tarjeta y se copia para mandarselo por WhatsApp. Pago unico del ciclo, salvo con
    // «Se renueva solo» (16-10): entonces Stripe le cobra solo cada ciclo.
    const [generandoEnlace, setGenerandoEnlace] = useState(null);
    // Y `pagoSemanas` 4: «Pago 4 semanas» (16-10), que siempre se renueva solo.
    const generarEnlacePago = async (l, plan = 'plan_12en12', recurrente = false, pagoSemanas = undefined) => {
        setGenerandoEnlace(l.id);
        try {
            const r = await api.post(`/leads/${l.id}/enlace-pago`, { plan, recurrente, pago_semanas: pagoSemanas });
            const url = r.data?.url;
            // Con `siempre` (16-10): desde el PR 5 los avisos verdes van mudos, y este es el que
            // dice que el enlace ya está copiado, o el que lo enseña si no se pudo copiar.
            try {
                await navigator.clipboard.writeText(url);
                toast.success(`Enlace de pago copiado (${euros(r.data.importe_eur)}). Pégaselo por WhatsApp.`, { siempre: true });
            } catch {
                // Sin permiso de portapapeles (http, o el navegador lo bloquea): se enseña
                // para copiar a mano, que es mejor que perder el enlace recien creado.
                toast.success('Enlace de pago creado', { description: url, duration: 30000, siempre: true });
            }
            setLlamadas(prev => prev.filter(x => x.id !== l.id));
        } catch (e) {
            toast.error(mensajeDeError(e, 'No se pudo crear el enlace de pago'));
        } finally {
            setGenerandoEnlace(null);
        }
    };

    // Los que van uno a uno (cuestan dinero, los mira una persona) y los que van contados.
    // Los graves primero: un cobro fallido antes que una tarjeta que caduca el mes que viene.
    // Los de reporte vencido ya no se crean (17-10); si queda alguno viejo, no se enseña.
    const avisosSueltos = avisos
        .filter(a => a.type !== 'report_overdue')
        .sort((a, b) => (a.severity === 'critical' ? 0 : 1) - (b.severity === 'critical' ? 0 : 1));

    // Dar por visto un aviso. Desaparece de la lista en cuanto se pulsa: la respuesta del
    // servidor no cambia nada en pantalla y esperarla solo hace que el botón parezca roto.
    const cerrarAviso = async (aviso) => {
        setAvisos(prev => prev.filter(a => a.id !== aviso.id));
        try {
            await api.post(`/admin/stripe/alerts/${aviso.id}/resolve`);
        } catch {
            toast.error('No se pudo cerrar el aviso');
            setAvisos(prev => [aviso, ...prev]);   // se devuelve a su sitio
        }
    };

    const resolverRevision = async (rev) => {
        try {
            await api.post(`/admin/macro-revisiones/${rev.id}/resolver`);
            setRevisiones(prev => prev.filter(r => r.id !== rev.id));
            toast.success('Revisión marcada como revisada');
        } catch {
            toast.error('No se pudo marcar la revisión');
        }
    };

    const markReport = async (item, enviado) => {
        try {
            await api.post('/admin/report-cadence/mark', {
                client_id: item.client_id, tipo: item.tipo, due_date: item.due_date, enviado,
            });
            setCadence(prev => prev.map(i =>
                i.client_id === item.client_id && i.tipo === item.tipo && i.due_date === item.due_date
                    ? { ...i, status: enviado ? 'enviado' : 'pendiente' }
                    : i
            ));
            toast.success(enviado ? 'Reporte marcado como enviado' : 'Marca de envío quitada');
        } catch {
            toast.error('No se pudo actualizar el reporte');
        }
    };

    // LA CAMPANA DEL EQUIPO LA LLEVA EL LAYOUT (I6, 12-10): su estado vive en
    // `useCampanaDelEquipo`, lo crea `AdminLayout` una sola vez y llega aquí por el contexto
    // del Outlet. Así la campana de la cabecera estrecha y la de esta portada son la misma.
    // Las peticiones de compra se pintan además como tarjeta arriba, con esa misma lista.
    const { campana } = useOutletContext();
    const { avisosEquipo, abrirAviso: abrirAvisoEquipo, marcarAviso: marcarAvisoEquipo } = campana;

    // El reparto por plan enseña los que se venden; los antiguos, aquí detrás.
    const [verAntiguos, setVerAntiguos] = useState(false);

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const [statsRes, upcomingRes, clientsRes, cadenceRes, revisionesRes, todoRes, llamadasRes, semanaRes] = await Promise.all([
                    api.get('/admin/dashboard-stats'),
                    // Los próximos cobros son dinero: al entrenador ni se le piden (el
                    // backend le contestaría 403 igualmente).
                    esAdmin ? api.get('/admin/upcoming-payments') : Promise.resolve({ data: { upcoming: [] } }),
                    api.get('/admin/clients'),
                    api.get('/admin/report-cadence'),
                    api.get('/admin/macro-revisiones').catch(() => ({ data: { items: [] } })),
                    api.get('/admin/todo-semana').catch(() => ({ data: null })),
                    api.get('/leads/llamadas-pendientes').catch(() => ({ data: { llamadas: [] } })),
                    // Va la ÚLTIMA a propósito: así se añade sin tocar el orden de las que
                    // ya estaban, que es de donde salen por posición las de arriba.
                    esAdmin ? api.get('/admin/stripe/la-semana-que-viene').catch(() => ({ data: null }))
                            : Promise.resolve({ data: null }),
                ]);
                setStats(statsRes.data);
                setUpcoming(upcomingRes.data.upcoming || []);
                setClients(clientsRes.data || []);
                setCadence(cadenceRes.data.items || []);
                setRevisiones(revisionesRes.data.items || []);
                setTodo(todoRes.data || null);
                setLlamadas(llamadasRes.data?.llamadas || []);
                setSemana(semanaRes?.data || null);

                // Los avisos del sistema (cobros fallidos, bajas) se piden DESPUÉS y no dentro
                // del grupo de arriba: la llamada a la cadencia cierra los de reporte vencido
                // que quedaban (17-10), así que pedidos a la vez se enseñaría la lista de antes.
                // Los avisos de pagos (cobros fallidos, bajas por impago, tarjetas) son
                // dinero: solo para el admin (P62).
                if (esAdmin) {
                    try {
                        const avisosRes = await api.get('/admin/stripe/alerts', { params: { resolved: false } });
                        setAvisos(Array.isArray(avisosRes.data) ? avisosRes.data : []);
                    } catch { /* sin avisos: la tarjeta se queda vacía, no rompe el panel */ }
                }
            } catch (error) {
                console.error('Error fetching dashboard:', error);
                toast.error('Error al cargar dashboard');
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, [api, esAdmin]);

    if (loading) {
        return (
            <div className="p-4 md:p-6 bg-[#0A0A0A] min-h-screen">
                <div className="animate-pulse space-y-4">
                    <div className="h-8 bg-[#222] rounded w-1/2 md:w-1/4" />
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                        {[1,2,3,4,5].map(i => <div key={i} className="h-28 bg-[#111] rounded-xl" />)}
                    </div>
                    <div className="h-48 bg-[#111] rounded-xl" />
                </div>
            </div>
        );
    }

    // Todos los planes con clientes activos, mayor a menor.
    const planEntries = Object.entries(stats?.plans || {}).sort((a, b) => b[1] - a[1]);
    const totalPlanActive = planEntries.reduce((a, [, n]) => a + n, 0);
    const planLabel = (code) => planCatalog?.[code]?.name || (code === 'sin_plan' ? 'Sin plan' : code);
    const planColor = (code) => PLAN_COLORS[code] || '#666666';
    const pendingReports = cadence.filter(i => i.status !== 'enviado');

    // Los que están a la venta, cada uno con su trozo. Los que ya no se venden se juntan en
    // uno solo: siguen contando en el total y siguen listados debajo, pero no se llevan
    // catorce franjas de la barra. Sin minWidth: el ancho es el porcentaje y nada más, que
    // para eso es una barra de reparto.
    const pctDe = (n) => (totalPlanActive > 0 ? (n / totalPlanActive) * 100 : 0);
    const seVende = (code) => planCatalog?.[code]?.estado === 'activo';
    const antiguos = planEntries.filter(([plan]) => !seVende(plan));
    const antiguosTotal = antiguos.reduce((a, [, n]) => a + n, 0);
    // LA BARRA PINTA LOS PLANES QUE TIENE LA GENTE, NO LOS QUE ESTÁN A LA VENTA
    // (Francisco, 17-08: «no hay distribución de color, el 176 está en gris»).
    //
    // Se dibujaba un trozo por plan EN VENTA y el resto en un gris común. Como hoy los
    // cuatro que se venden tienen cero clientes -- todo el mundo viene de Calma y está en un
    // plan legacy --, la barra entera era un solo bloque gris con el total dentro: un
    // gráfico de reparto que no repartía nada. Ahora salen los seis mayores con su color y
    // la cola se junta, que es lo que decía este comentario desde el principio.
    const TOPE_BARRA = 6;
    const conGente = planEntries.filter(([, count]) => count > 0);
    const cola = conGente.slice(TOPE_BARRA);
    const colaTotal = cola.reduce((a, [, n]) => a + n, 0);
    const barraPlanes = [
        ...conGente.slice(0, TOPE_BARRA).map(([plan, count]) => ({
            plan, count, pct: pctDe(count), color: planColor(plan), label: planLabel(plan),
        })),
        ...(colaTotal > 0 ? [{
            plan: '__cola__', count: colaTotal, pct: pctDe(colaTotal), color: '#3F3F46',
            label: `Otros ${cola.length} planes: ${cola.map(([p, n]) => `${planLabel(p)} ${n}`).join(' · ')}`,
        }] : []),
    ];

    return (
        <div className="p-4 md:p-6 space-y-5 md:space-y-6 animate-fade-in bg-[#0A0A0A] min-h-screen" data-testid="admin-dashboard">
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
                <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight min-w-0" style={{ fontFamily: 'Barlow Condensed' }}>PANEL DE CONTROL</h1>
                {/* Con la ventana estrecha la campana va en la cabecera del layout (la de la
                    hamburguesa): aquí solo se pinta desde 1.024 px, para no tener dos. */}
                <div className="hidden lg:block flex-shrink-0">
                    <CampanaDelEquipo campana={campana} />
                </div>
            </div>

            {/* Piden comprar: lo de la campana, además, aquí fuera. Un cliente que marca la
                rutina del mes o «Cuéntame el Silver» está pidiendo comprar, y eso no puede
                depender de que alguien abra un desplegable. */}
            <PeticionesDeCompra estado={avisosEquipo} onAbrir={abrirAvisoEquipo}
                onAtendida={marcarAvisoEquipo} />

            {/* Piden llamada (Nivel 3): por encima de los KPIs porque hay gente esperando.
                Oculta desde el 19-10 (tarea 10 de la hoja del 15-10: se lleva por fuera); se
                vuelve a enseñar en `lib/pantallasDelPanel`. */}
            {seEnsenanLasLlamadas() && (
                <LlamadasPendientes llamadas={llamadas} onAtendida={marcarLlamadaAtendida}
                    onCobrar={generarEnlacePago} generandoEnlace={generandoEnlace}
                    onPonerPago={ponerPagoPendiente} onPagoResuelto={marcarPagoResuelto} esAdmin={esAdmin}
                    planCatalog={planCatalog}
                    onVerFicha={(l) => navigate(`/admin/clients/${l.cliente_id}`)} />
            )}

            {/* KPI Row */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="kpi-row">
                {/* LA SUMA, A LA VISTA (Jesús, 11-09, punto 22).
                    Él contó «167 totales, 104 activos y 45 caducados: son 149, faltan 18».
                    No faltaban: los 18 son las bajas y los otros, que están en sus propias
                    tarjetas -- una de ellas con la letra pequeña rota, que es el punto 23 --.
                    Los cuatro cajones son excluyentes y el total cuadra siempre con ellos,
                    pero eso había que descubrirlo sumando de cabeza cuatro casillas de la
                    fila. Ahora lo dice el propio total. */}
                <KpiCard value={stats?.total_clients || 0} label="Clientes totales" icon={Users} color="#FF671F" testId="kpi-total"
                    pie={stats ? (
                        <span className="text-[10px] text-white/50 mt-0.5" data-testid="kpi-total-reparto">
                            {[
                                [stats.active_clients, 'con acceso'],
                                [stats.caducados_clients, 'caducados'],
                                [stats.inactive_clients, 'bajas'],
                                [stats.otros_clients, 'otros'],
                            ].filter(([n]) => n > 0).map(([n, q]) => `${n} ${q}`).join(' · ')}
                        </span>
                    ) : null} />
                {/* P63: activos = con acceso vigente, el MISMO criterio que la lista de
                    Clientes (solo activos desde el 19-10). Los marcados «activo» sin acceso
                    van debajo, con su nombre, para que este número cuadre con aquella lista. */}
                <KpiCard value={stats?.active_clients || 0} label="Activos" icon={UserCheck} color="#22C55E" testId="kpi-active"
                    pie={stats?.caducados_clients > 0 ? (
                        <span className="text-[10px] text-white/50 mt-0.5" data-testid="kpi-caducados">
                            y {stats.caducados_clients} caducados
                        </span>
                    ) : null} />
                {/* Antes ponía "En riesgo" y saltaba para tres de cada cuatro activos, así
                    que no era una alerta: era el color de fondo de la pantalla. Y sobre
                    todo no decía EN QUÉ. Ahora el número son los que tienen algo en rojo, y
                    debajo va desglosado por celda, que es lo accionable (punto 32). */}
                <KpiCard value={stats?.at_risk_clients || 0} label="Con algo en rojo" icon={AlertTriangle} color="#EAB308" testId="kpi-risk"
                    pie={stats?.semaforo && (
                        <span className="flex flex-wrap items-center gap-x-2 text-[10px] tabular-nums text-white/50 mt-0.5" data-testid="kpi-semaforo">
                            <span>reporte <b className="text-red-400">{stats.semaforo.reporte?.malo || 0}</b></span>
                            <span>peso <b className="text-red-400">{stats.semaforo.peso?.malo || 0}</b></span>
                            <span>contacto <b className="text-red-400">{stats.semaforo.contacto?.malo || 0}</b></span>
                            <span>ajuste <b className="text-red-400">{stats.semaforo.ajuste?.malo || 0}</b></span>
                            <span>perfil <b className="text-red-400">{stats.semaforo.perfil_largo?.malo || 0}</b></span>
                        </span>
                    )} />
                {/* Punto 2.4g: «"0 bajas" con 188 totales y 184 activos: faltan cuatro por
                    algún sitio». Los que no son ni activos ni bajas -- pendiente de pago,
                    checkout sin terminar -- no aparecían en ninguna casilla, y un total que
                    no cuadra con sus partes tira por tierra el panel entero. */}
                <KpiCard value={stats?.inactive_clients || 0} label="Bajas" icon={UserMinus} color="#EF4444" testId="kpi-bajas"
                    pie={stats?.otros_clients ? (
                        // Y CON NOMBRE, que la suma ya cuadraba pero «ni activos ni de
                        // baja» no le dice a nadie qué hacer con ellos (punto 63, QA del
                        // 15-08). «4 pendientes de pago · 1 registrado» sí.
                        // Y ESCRITO ENTERO (Jesús, 11-09, punto 23). Salía «+ 2 2 registrado»:
                        // el total y detrás el desglose, sin nada en medio y con el número
                        // repetido, porque el desglose ya suma el total. Ahora, con un solo
                        // estado se dice una vez -- «+ 2 registrados» -- y con varios se
                        // pone el total delante y dos puntos: «+ 5: 4 pendientes de pago ·
                        // 1 registrado». Y el plural, que decía «2 registrado».
                        <span className="text-[10px] text-white/50 mt-0.5" data-testid="kpi-otros">
                            {(() => {
                                const partes = stats.otros_por_estado || [];
                                const enPlural = (n, palabra) => (n === 1 || /s$/.test(palabra)
                                    ? palabra : `${palabra}s`);
                                if (!partes.length) return `+ ${stats.otros_clients} ni activos ni de baja`;
                                const lista = partes.map((o) => {
                                    const nombre = prettyToken(o.estado).toLowerCase();
                                    return `${o.n} ${enPlural(o.n, nombre)}`;
                                });
                                return partes.length === 1
                                    ? `+ ${lista[0]}`
                                    : `+ ${stats.otros_clients}: ${lista.join(' · ')}`;
                            })()}
                        </span>
                    ) : null} />
                {/* El MRR es dinero: solo admin (P62). Al entrenador el backend ni se lo manda.
                    Y ES EL MISMO NÚMERO QUE «FACTURA AL MES» DE PANELES > DIRECCIÓN (punto 46
                    del 24-08): eran dos cuentas distintas y daban 20.062 € y 31.945 € de los
                    mismos clientes el mismo día. Ahora las dos salen de la misma función y del
                    mismo cobro real, así que si vuelven a discrepar es que algo se ha roto. */}
                {/* Y ESCRITO IGUAL QUE ALLÍ, que si no la comprobación no se puede hacer de
                    un vistazo: aquí ponía «22485.53€» y Dirección «22.486 €». Es el mismo
                    dinero, pero con el punto de los decimales a la inglesa y sin miles no lo
                    parece, y el punto 46 va justo de leer dos pantallas y creerlas. */}
                {esAdmin && (
                    <KpiCard value={eurosRedondos(stats?.mrr || 0)} label="MRR" icon={DollarSign} color="#8B5CF6" testId="kpi-mrr"
                        pie={stats?.mrr_criterio && (
                            <span className="text-[10px] text-white/50 mt-0.5" data-testid="kpi-mrr-criterio">
                                {stats.mrr_criterio.clientes} con acceso · {stats.mrr_criterio.cobro_real} con cobro real
                            </span>
                        )} />
                )}
            </div>

            {/* "Esta semana te tocan estos seis" (punto 29). Va antes del resto del panel:
                es lo primero que se pregunta un lunes. */}
            <EstaSemanaTeTocan items={todo?.te_tocan} navigate={navigate} />

            {/* Por hacer esta semana (tarea 19) */}
            <TodoSemana todo={todo} soloAlCorriente={soloAlCorriente} setSoloAlCorriente={setSoloAlCorriente}
                forzarRutina={verComo(user) === 'sin_rutina'}
                navigate={navigate} />

            {/* Plan Distribution */}
            <Card className="bg-[#111111] border-[#222]" data-testid="plan-distribution">
                <CardContent className="p-5">
                    <p className="text-xs font-bold text-white/40 uppercase tracking-wider mb-4">Distribución por plan</p>
                    <div className="flex flex-col gap-3">
                        {/* LA BARRA MENTÍA CON 17 PLANES.
                            Cada trozo llevaba minWidth 40px, así que los planes de uno o dos
                            clientes se inflaban hasta ocupar lo mismo que uno de cuarenta, la
                            suma se pasaba del ancho y el reparto que se veía no era el real.
                            Ahora la barra dibuja los seis primeros a escala de verdad y junta
                            la cola en un trozo gris, que es como se lee un reparto. El detalle
                            no se pierde: la leyenda de abajo sigue listando los 17 con su
                            número (Jesús, 11-08). */}
                        <div className="w-full flex h-8 rounded-lg overflow-hidden bg-[#1A1A1A]">
                            {barraPlanes.map(({ plan, count, pct, color, label }) => (
                                <div
                                    key={plan}
                                    className="h-full flex items-center justify-center text-xs font-bold transition-all overflow-hidden"
                                    style={{ width: `${pct}%`, backgroundColor: color, color: '#fff' }}
                                    title={`${label}: ${count}`}
                                >
                                    {/* Por debajo de un 6% no cabe el número sin pisar al vecino. */}
                                    {pct >= 6 ? count : ''}
                                </div>
                            ))}
                        </div>
                        {/* La leyenda es la de la barra: los mismos seis y sus colores. Antes
                            listaba solo los planes en venta y, con todos los clientes en planes
                            legacy, se quedaba vacía debajo de una barra llena. El resto sigue
                            detrás del desplegable de abajo, con su número. */}
                        <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                            {barraPlanes.filter(({ plan }) => plan !== '__cola__').map(({ plan, count }) => (
                                <div key={plan} className="flex items-center gap-1.5">
                                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: planColor(plan) }} />
                                    <span className="text-xs text-white/50 uppercase">{planLabel(plan)}</span>
                                    <span className="text-xs font-bold text-white">{count}</span>
                                    {!seVende(plan) && <span className="text-[10px] text-white/25">legacy</span>}
                                </div>
                            ))}
                        </div>
                        {antiguos.length > 0 && (
                            <div>
                                <button type="button" onClick={() => setVerAntiguos(v => !v)}
                                    data-testid="ver-planes-antiguos"
                                    className="text-xs text-white/40 hover:text-white/70 underline underline-offset-2">
                                    {verAntiguos
                                        ? 'Ocultar los planes que ya no se venden'
                                        : `${antiguos.length === 1 ? 'Ver el plan que ya no se vende' : `Ver los ${antiguos.length} planes que ya no se venden`} (${plural(antiguosTotal, 'cliente')})`}
                                </button>
                                {verAntiguos && (
                                    <div className="flex flex-wrap gap-x-3 gap-y-1.5 mt-2.5">
                                        {antiguos.map(([plan, count]) => (
                                            <div key={plan} className="flex items-center gap-1.5">
                                                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: planColor(plan) }} />
                                                <span className="text-xs text-white/40 uppercase">{planLabel(plan)}</span>
                                                <span className="text-xs font-bold text-white/70">{count}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Reportes del coach que tocan esta semana.
                OCULTO temporalmente (a petición del usuario, 20-07): reactivar quitando el
                `false &&` cuando el panel esté mejorado. */}
            {false && (
            <Card className="bg-[#111111] border-[#222]" data-testid="report-cadence">
                <CardHeader className="pb-3">
                    <CardTitle className="flex items-center justify-between">
                        <span className="text-base text-white uppercase tracking-wider flex items-center gap-2">
                            <FileText className="w-4 h-4 text-[#FF671F]" />
                            Reportes de esta semana
                        </span>
                        <Badge className={`border-0 text-xs ${pendingReports.length > 0 ? 'bg-[#FF671F]/20 text-[#FF671F]' : 'bg-green-500/10 text-green-500'}`}>
                            {pendingReports.length > 0 ? `${pendingReports.length} por enviar` : 'Al día'}
                        </Badge>
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                    {cadence.length === 0 ? (
                        <p className="text-white/30 text-sm text-center py-4">Ningún cliente tiene reporte programado esta semana</p>
                    ) : (
                        <div className="space-y-2">
                            {cadence.map((item, i) => (
                                <div key={`${item.client_id}-${item.tipo}-${item.due_date}`}
                                    className="flex items-center justify-between gap-2 p-3 bg-[#0A0A0A] rounded-lg border border-[#222] hover:border-[#FF671F]/30 transition-colors"
                                    data-testid={`cadence-${i}`}>
                                    <div className="flex items-center gap-3 min-w-0">
                                        <Badge className={`border-0 text-[10px] flex-shrink-0 ${
                                            item.status === 'vencido' ? 'bg-red-500/15 text-red-400'
                                            : item.status === 'enviado' ? 'bg-green-500/10 text-green-500'
                                            : 'bg-yellow-500/10 text-yellow-500'
                                        }`}>
                                            {item.status}
                                        </Badge>
                                        <div className="min-w-0 cursor-pointer" onClick={() => navigate(`/admin/clients/${item.client_id}`)}>
                                            <p className="text-white text-sm font-medium truncate">{item.client_name || item.client_email}</p>
                                            <p className="text-white/40 text-xs truncate">
                                                {item.tipo_label} · {item.due_label} {pintarDia(item.due_date, { day: 'numeric', month: 'short' })} · semana {item.week}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                                        <span className="hidden sm:inline"><PlanBadge plan={item.plan} planName={item.plan_name} /></span>
                                        {item.status === 'enviado' ? (
                                            <Button variant="ghost" size="sm" className="text-white/40 hover:text-white text-xs uppercase"
                                                onClick={() => markReport(item, false)}>
                                                Desmarcar
                                            </Button>
                                        ) : (
                                            <Button size="sm" className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white text-xs uppercase"
                                                onClick={() => markReport(item, true)} data-testid={`mark-sent-${i}`}>
                                                Marcar enviado
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
            )}

            {/* AVISOS DEL SISTEMA. Solo sale si hay alguno: una tarjeta que pone «todo bien»
                todos los días acaba siendo invisible el día que pone otra cosa.

                LOS DE REPORTE VENCIDO YA NO SALEN (17-10). Eran uno por cliente y semana, 95 de 99
                avisos, y miraban si el equipo marcó el reporte del coach, no si el cliente mandó el
                suyo. Quién va con retraso de verdad ya se ve en «Por hacer esta semana».

                Lo que sí va uno a uno es lo que cuesta dinero y necesita a una persona: un
                cobro que ha fallado, una baja automática o una tarjeta caducada. */}
            {esAdmin && avisosSueltos.length > 0 && (
            <Card className="bg-[#111111] border-[#222]" data-testid="avisos-sistema">
                <CardHeader className="pb-3">
                    <CardTitle className="flex items-center justify-between">
                        <span className="text-base text-white uppercase tracking-wider flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-[#FF671F]" />
                            Avisos
                        </span>
                        <Badge className={`border-0 text-xs ${avisosSueltos.length > 0 ? 'bg-[#FF671F]/20 text-[#FF671F]' : 'bg-green-500/10 text-green-500'}`}>
                            {avisosSueltos.length > 0
                                ? (avisosSueltos.length === 1 ? '1 por mirar' : `${avisosSueltos.length} por mirar`)
                                : 'Nada urgente'}
                        </Badge>
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                    <div className="space-y-2">
                        {avisosSueltos
                            .map((aviso, i) => {
                                const grave = aviso.severity === 'critical';
                                const cliente = clients.find(c => c.id === aviso.client_id);
                                return (
                                    <div key={aviso.id} data-testid={`aviso-${i}`}
                                        className={`flex items-start justify-between gap-3 p-3 bg-[#0A0A0A] rounded-lg border transition-colors ${
                                            grave ? 'border-red-500/40 hover:border-red-500/70' : 'border-[#222] hover:border-[#FF671F]/30'}`}>
                                        <div className="min-w-0 cursor-pointer"
                                            onClick={() => aviso.client_id && navigate(`/admin/clients/${aviso.client_id}`)}>
                                            <p className="text-white text-sm font-medium truncate">
                                                {aviso.title}
                                                {cliente?.name && <span className="text-white/40 font-normal"> · {cliente.name}</span>}
                                            </p>
                                            <p className="text-white/40 text-xs">{aviso.message}</p>
                                            <p className="text-white/25 text-[11px] mt-0.5">
                                                {new Date(aviso.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                                            </p>
                                        </div>
                                        <Button size="sm" variant="ghost" data-testid={`aviso-cerrar-${i}`}
                                            className="text-white/50 hover:text-white hover:bg-white/10 text-xs uppercase flex-shrink-0"
                                            onClick={() => cerrarAviso(aviso)}>
                                            Visto
                                        </Button>
                                    </div>
                                );
                            })}
                    </div>

                </CardContent>
            </Card>
            )}

            {/* Macros por revisar (motor v2): dieta reportada que no cuadra con lo recomendado */}
            <Card className="bg-[#111111] border-[#222]" data-testid="macro-revisiones">
                <CardHeader className="pb-3">
                    <CardTitle className="flex items-center justify-between">
                        <span className="text-base text-white uppercase tracking-wider flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-[#FF671F]" />
                            Macros por revisar
                        </span>
                        <Badge className={`border-0 text-xs ${revisiones.length > 0 ? 'bg-[#FF671F]/20 text-[#FF671F]' : 'bg-green-500/10 text-green-500'}`}>
                            {revisiones.length > 0 ? `${revisiones.length} pendientes` : 'Al día'}
                        </Badge>
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                    {revisiones.length === 0 ? (
                        <p className="text-white/30 text-sm text-center py-4">Ninguna dieta reportada pendiente de revisar</p>
                    ) : (
                        <div className="space-y-2">
                            {revisiones.map((rev, i) => (
                                <div key={rev.id}
                                    className="flex items-center justify-between gap-2 p-3 bg-[#0A0A0A] rounded-lg border border-[#222] hover:border-[#FF671F]/30 transition-colors"
                                    data-testid={`revision-${i}`}>
                                    <div className="min-w-0 cursor-pointer" onClick={() => rev.client_id && navigate(`/admin/clients/${rev.client_id}`)}>
                                        <p className="text-white text-sm font-medium truncate">{rev.client_name}</p>
                                        <p className="text-white/40 text-xs truncate">
                                            Come {Math.round(rev.comparacion?.hc_reportados || 0)} g de HC · recomendado {rev.comparacion?.hc_recomendados} g
                                            · diferencia {rev.comparacion?.diferencia > 0 ? '+' : ''}{rev.comparacion?.diferencia} g
                                            · {new Date(rev.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                                        </p>
                                    </div>
                                    {/* «Revisada» a secas se leia como el estado de la fila,
                                        no como el boton que la resuelve. */}
                                    <Button size="sm" className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white text-xs uppercase flex-shrink-0"
                                        onClick={() => resolverRevision(rev)} data-testid={`revision-resolver-${i}`}>
                                        Marcar revisada
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* LA SEMANA QUE VIENE (reunión del 4-09). La pregunta que Jesús le hace a Jenny
                todos los días: «¿a quién acaba la semana que viene la suscripción?». Va
                ENCIMA de los próximos cobros porque el que se va es el que hay que atender:
                el que renueva no necesita nada. */}
            {esAdmin && semana && (
            <Card className="bg-[#111111] border-[#222]" data-testid="la-semana-que-viene">
                <CardHeader className="pb-3">
                    <CardTitle className="flex items-center justify-between flex-wrap gap-2">
                        <span className="text-base text-white uppercase tracking-wider flex items-center gap-2">
                            <CalendarClock className="w-4 h-4 text-[#FF671F]" />
                            La semana que viene
                        </span>
                        <div className="flex gap-1">
                            {[
                                ['se_acaban', 'Se acaban', semana.resumen.se_acaban],
                                ['renuevan', 'Renuevan', semana.resumen.renuevan],
                                ['entraron', 'Entraron', semana.resumen.entraron],
                            ].map(([clave, texto, n]) => (
                                <button key={clave} onClick={() => setVerSemana(clave)}
                                    data-testid={`semana-tab-${clave}`}
                                    className={`px-2.5 py-1 rounded-lg text-xs border transition-colors ${
                                        verSemana === clave
                                            ? 'bg-[#FF671F]/20 text-[#FF671F] border-[#FF671F]/40'
                                            : 'bg-[#0A0A0A] text-white/50 border-[#222] hover:border-[#FF671F]/30'}`}>
                                    {texto} <span className="font-bold">{n}</span>
                                </button>
                            ))}
                        </div>
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                    <p className="text-white/30 text-xs mb-3">
                        {verSemana === 'entraron'
                            ? `Los que empezaron su ciclo en los últimos ${semana.dias} días.`
                            : `${delAl(semana.desde, semana.hasta)}. ${semana.resumen.automaticos} ${semana.resumen.automaticos === 1 ? 'paga solo' : 'pagan solos'} por Stripe y ${semana.resumen.manuales} no.`}
                    </p>
                    {(semana[verSemana] || []).length === 0 ? (
                        <p className="text-white/30 text-sm text-center py-4">Aquí no hay nadie esta semana</p>
                    ) : (
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                            {(semana[verSemana] || []).map((f, i) => (
                                <div key={f.client_id + i}
                                    className="flex items-center justify-between gap-2 p-3 bg-[#0A0A0A] rounded-lg border border-[#222]"
                                    data-testid={`semana-${verSemana}-${i}`}>
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-9 h-9 bg-[#FF671F]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <span className="text-[#FF671F] font-bold text-sm" style={{ fontFamily: 'Barlow Condensed' }}>
                                                {/* Los que entraron ya pasaron: «-3d» se lee como una cuenta
                                                    atrás rota, «hace 3d» se lee a la primera. */}
                                                {f.dias == null ? '?' : f.dias < 0 ? `hace ${-f.dias}d` : `${f.dias}d`}
                                            </span>
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-white text-sm font-medium truncate">{f.nombre}</p>
                                            <p className="text-white/40 text-xs truncate">
                                                {f.motivo}
                                                {' · '}
                                                {/* Las DOS semanas, que Jesús subraya que son distintas. Un
                                                    hueco quiere decir que no se sabe, no que sea la 1. */}
                                                {/* CON PALABRAS, NO CON UNA INTERROGACIÓN (Jesús,
                                                    11-09, punto 25): las cuatro renovaciones ponían
                                                    «rutina ?» y no se sabía si era un fallo o un dato
                                                    que falta. */}
                                                programa {f.semana_programa ?? '?'} · {f.semana_rutina != null
                                                    ? `rutina ${f.semana_rutina}` : 'sin rutina puesta'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                                        <span className={`text-xs px-2 py-0.5 rounded border ${
                                            f.cobro === 'automatico'
                                                ? 'text-emerald-300/80 border-emerald-500/30 bg-emerald-500/10'
                                                : 'text-amber-300/80 border-amber-500/30 bg-amber-500/10'}`}>
                                            {f.cobro === 'automatico' ? 'automático' : 'manual'}
                                        </span>
                                        <PlanBadge plan={f.plan} />
                                        <span className="text-[#FF671F] font-bold text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
                                            {/* Un precio de 0 en la ficha es «no se sabe», no «no paga»: se
                                                enseña como hueco, igual que lo cuenta el resumen. */}
                                            {eurosOHueco(f.importe)}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {verSemana !== 'entraron' && (
                        <p className="text-white/30 text-xs mt-3">
                            {verSemana === 'se_acaban'
                                ? `Se van ${euros(semana.resumen.importe_que_se_va)}`
                                : `Renuevan ${euros(semana.resumen.importe_que_renueva)}`}
                            {(verSemana === 'se_acaban' ? semana.resumen.se_acaban_sin_precio : semana.resumen.renuevan_sin_precio) > 0 &&
                                ` · ${verSemana === 'se_acaban' ? semana.resumen.se_acaban_sin_precio : semana.resumen.renuevan_sin_precio} sin precio en la ficha`}
                        </p>
                    )}
                </CardContent>
            </Card>
            )}

            {/* Upcoming Payments: importes de cobro, solo admin (P62) */}
            {esAdmin && (
            <Card className="bg-[#111111] border-[#222]" data-testid="upcoming-payments">
                <CardHeader className="pb-3">
                    <CardTitle className="flex items-center justify-between">
                        <span className="text-base text-white uppercase tracking-wider flex items-center gap-2">
                            <CreditCard className="w-4 h-4 text-[#FF671F]" />
                            Próximos cobros (7 días)
                        </span>
                        <Badge className="bg-[#FF671F]/20 text-[#FF671F] border-0 text-xs">{upcoming.length}</Badge>
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                    {upcoming.length === 0 ? (
                        <p className="text-white/30 text-sm text-center py-4">No hay cobros programados en los próximos 7 días</p>
                    ) : (
                        <div className="space-y-2">
                            {upcoming.map((u, i) => {
                                // El cobro es un DÍA: ni se pinta ni se cuenta con el huso del navegador.
                                const payDate = diaDeCalendario(u.next_payment);
                                const daysLeft = payDate ? -diasDesdeHoy(payDate) : '?';
                                return (
                                    <div key={i} className="flex items-center justify-between gap-2 p-3 bg-[#0A0A0A] rounded-lg border border-[#222] hover:border-[#FF671F]/30 transition-colors" data-testid={`upcoming-${i}`}>
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-9 h-9 bg-[#FF671F]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                                <span className="text-[#FF671F] font-bold text-sm" style={{ fontFamily: 'Barlow Condensed' }}>{daysLeft}d</span>
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-white text-sm font-medium truncate">{u.name}</p>
                                                <p className="text-white/40 text-xs">{pintarDia(payDate, { day: 'numeric', month: 'short' }, '-')}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                                            <PlanBadge plan={u.plan} />
                                            <span className="text-[#FF671F] font-bold text-lg" style={{ fontFamily: 'Barlow Condensed' }}>{eurosOHueco(u.price)}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>
            )}

            {/* Client List (compact) */}
            <Card className="bg-[#111111] border-[#222]" data-testid="client-list-compact">
                <CardHeader className="pb-3">
                    <CardTitle className="flex items-center justify-between">
                        <span className="text-base text-white uppercase tracking-wider flex items-center gap-2">
                            <Users className="w-4 h-4 text-[#FF671F]" />
                            Clientes ({clients.filter(esDeLaLista).length})
                        </span>
                        <Button variant="ghost" size="sm" className="text-[#FF671F] hover:bg-[#FF671F]/10 uppercase text-xs" onClick={() => navigate('/admin/clients')}>
                            Ver todos <ChevronRight className="w-3 h-3 ml-1" />
                        </Button>
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                    <div className="space-y-1.5">
                        {/* Las filas son los mismos que cuenta el título (14-10, PAG-13): decía
                            «Clientes (1)» con dos filas debajo, la del cliente y tu propia ficha.
                            Y SOLO LOS ACTIVOS, con la regla de la lista a la que lleva «Ver
                            todos» (Francisco, 19-10): con caducados y bajas el número de aquí
                            no coincidía con el de la pantalla Clientes. */}
                        {clients.filter(esDeLaLista).slice(0, 8).map(c => (
                            <div
                                key={c.id}
                                className="flex items-center justify-between gap-2 p-2.5 rounded-lg hover:bg-white/5 cursor-pointer transition-colors"
                                onClick={() => navigate(`/admin/clients/${c.id}`)}
                                data-testid={`client-row-${c.id}`}
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-8 h-8 bg-[#222] rounded-lg flex items-center justify-center flex-shrink-0">
                                        <span className="text-[#FF671F] font-bold text-xs">{c.user?.name?.charAt(0)}</span>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-white text-sm font-medium truncate">{c.user?.name}</p>
                                        <p className="text-white/30 text-xs truncate">{c.user?.email}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <PlanBadge plan={c.plan} />
                                    {(() => {
                                        const e = estadoDeAcceso(c);
                                        return (
                                            <Badge className={`${e.tono === 'ok' ? 'bg-green-500/10 text-green-500'
                                                : e.tono === 'aviso' ? 'bg-yellow-500/10 text-yellow-400'
                                                : 'bg-red-500/10 text-red-400'} border-0 text-[10px]`}>
                                                {e.texto}
                                            </Badge>
                                        );
                                    })()}
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

// KPI Card Component
const KpiCard = ({ value, label, icon: Icon, color, testId, pie = null }) => (
    <Card className="bg-[#111111] border-[#222]" data-testid={testId}>
        <CardContent className="p-4">
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-3xl font-bold mt-1" style={{ fontFamily: 'Barlow Condensed', color }}>{value}</p>
                    <p className="text-[10px] text-white/40 uppercase tracking-wider mt-1">{label}</p>
                    {pie}
                </div>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                    <Icon className="w-4 h-4" style={{ color }} />
                </div>
            </div>
        </CardContent>
    </Card>
);

// Admin Layout
// La etiqueta del rol, en la cabecera y junto al nombre. Estaba escrita fija («Admin») y a
// un entrenador le salía también; abajo salía el valor interno en inglés («trainer»).
const rolDelEquipo = (user) => (user?.role === 'trainer' ? 'Entrenador' : 'Admin');

const AdminLayout = () => {
    const { user, logout, api } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // La campana del equipo, una para todo el panel (I6, 12-10): se pinta en la cabecera
    // estrecha de aquí abajo y, desde 1.024 px, en la portada (que la recibe por el Outlet).
    // De ella salen también las insignias de Leads y Mensajes del menú y el aviso de lead
    // nuevo: es la única que pregunta por esos dos números (cada 60 s).
    const campana = useCampanaDelEquipo();
    const newLeadsCount = campana.notif.leads;
    const unreadMessages = campana.notif.messages;
    // Y LAS SUGERENCIAS DE ALIMENTO TAMBIÉN AVISAN (5-09, ficha 106 del repaso de Gonzalo:
    // «hay dos sugerencias pendientes; que el panel avise»). Un cliente propone un alimento,
    // se queda en «Alimentos» y ahí puede estar dos meses: la entrada del menú no decía nada
    // mientras «Leads» al lado sí llevaba su número. Lo mismo que ellos, en la misma barra.
    const [sugerenciasPendientes, setSugerenciasPendientes] = useState(0);
    const [moreOpen, setMoreOpen] = useState(false); // drawer "Más" en movil
    const prevLeadsCount = useRef(null);

    // Cerrar el drawer "Más" al navegar entre secciones
    useEffect(() => { setMoreOpen(false); }, [location.pathname]);
    // "Lead nuevo recibido": cuando el número de la campana SUBE respecto a la lectura
    // anterior. La primera lectura del servidor solo se apunta (no es un lead nuevo, es
    // entrar al panel), y antes de ella el 0 de arranque no cuenta.
    const leadsLeidos = campana.notif.leadsLeidos;
    useEffect(() => {
        if (!leadsLeidos || !seAvisanLosLeads()) return;
        if (prevLeadsCount.current !== null && newLeadsCount > prevLeadsCount.current) {
            toast.info(`Lead nuevo recibido · ${newLeadsCount} sin gestionar`, {
                action: { label: 'Ver', onClick: () => navigate('/admin/leads') },
            });
        }
        prevLeadsCount.current = newLeadsCount;
    }, [leadsLeidos, newLeadsCount, navigate]);
    // El sondeo propio del layout se queda solo con lo que la campana no pide.
    useEffect(() => {
        let active = true;
        const poll = async () => {
            try {
                const s = await api.get('/admin/food-suggestions/stats/summary');
                if (active) setSugerenciasPendientes(s.data?.pendientes || 0);
            } catch { /* silencioso: un entrenador sin permiso no lo ve y no pasa nada */ }
        };
        poll();
        const id = setInterval(poll, 60000);
        return () => { active = false; clearInterval(id); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api]);

    // adminOnly: solo el admin lo ve. Usuarios SALIO de esa lista el 7-09-2026 (Francisco):
    // los entrenadores la necesitaban para ver su propia ficha y se abrio entera.
    // LO QUE NO SE ENSEÑA DE MOMENTO (13-10: Rutinas y el Simulador de semanas) lo decide
    // `lib/pantallasDelPanel`, para admin y para entrenador. Sus entradas siguen escritas
    // aquí y sus rutas siguen existiendo: volver a enseñarlas es cambiar un valor allí.
    const navItems = entradasDelMenu([
        // Rediseño del 21-10: el Dashboard se ve en el menú pero no se puede abrir (apagado en
        // `lib/pantallasDelPanel`); el panel entra directo en Clientes (ver App.js).
        { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
        // El Gestor de clientes (tarea 10 de la hoja del 15-10): mensual en naranja y macros en
        // verde o en rojo, lo que Jesús mira el lunes o el martes.
        { path: '/admin/clients', icon: Users, label: 'Clientes' },
        // Las tareas (doc 19-08, apartado 05): lo que cada uno tiene hoy, lo vencido y lo
        // que asignó. Sustituye al «todo va por WhatsApp y no queda en ningún sitio».
        { path: '/admin/tareas', icon: ClipboardList, label: 'Tareas' },
        // Los cuatro paneles del doc 19-08: el entrenador entra y ve solo el suyo.
        { path: '/admin/paneles', icon: Gauge, label: 'Paneles' },
        { path: '/admin/planes', icon: Layers, label: 'Planes', adminOnly: true },
        // Los interruptores de la app (punto 64): estaban al final de Planes, donde nadie
        // los encontraba. Solo admin, que apagan pantallas a TODOS los clientes.
        { path: '/admin/ajustes', icon: SlidersHorizontal, label: 'Ajustes', adminOnly: true },
        { path: '/admin/usuarios', icon: UserCheck, label: 'Equipo' },
        { path: '/admin/pagos', icon: Receipt, label: 'Cobros', adminOnly: true },
        // El acceso desde la hoja de pagos y Stripe, cada noche (28-09). Solo admin.
        { path: '/admin/accesos', icon: ShieldCheck, label: 'Accesos', adminOnly: true },
        // Qué reglas se activan en cada semana y día de un cliente de prueba (30-09). Solo admin.
        { path: '/admin/simulador', icon: CalendarRange, label: 'Simulador de semanas', adminOnly: true },
        // El reloj de los clientes, en sombra al lado de ActiveCampaign (30-09). Solo admin.
        { path: '/admin/reloj', icon: Clock, label: 'Reloj', adminOnly: true },
        // Las reglas del ciclo, los planes y los correos, redactados del catálogo (07-10). Solo admin.
        { path: '/admin/documentacion', icon: BookOpen, label: 'Documentación', adminOnly: true },
        { path: '/admin/leads', icon: UserPlus, label: 'Leads' },
        { path: '/admin/messages', icon: MessageCircle, label: 'Mensajes' },
        { path: '/admin/routines', icon: Dumbbell, label: 'Rutinas' },
        { path: '/admin/menus', icon: Utensils, label: 'Menús' },
        { path: '/admin/alimentos', icon: Apple, label: 'Alimentos' },
    ]).filter((i) => !i.adminOnly || user?.role === 'admin');

    // En movil la barra inferior muestra los accesos principales + boton "Mas" (el resto va
    // al drawer). Leads salió el 26-10, al apagarse en el menú.
    const primaryPaths = ['/admin', '/admin/clients', '/admin/messages'];
    const primaryNav = navItems.filter((i) => primaryPaths.includes(i.path));
    const secondaryNav = navItems.filter((i) => !primaryPaths.includes(i.path));

    const isActive = (path, exact = false) => {
        if (exact) return location.pathname === path;
        return location.pathname.startsWith(path);
    };

    // Drawer móvil (menú hamburguesa), como en modo cliente
    const [drawerOpen, setDrawerOpen] = useState(false);
    useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

    // Barra inferior móvil: solo los 4 accesos principales; el resto va en el drawer
    const bottomItems = primaryNav;

    // EL NÚMERO DE CADA ENTRADA, EN UN SOLO SITIO. La barra de escritorio se lo pintaba
    // aparte, con un bloque por cada uno, y por eso al añadir el de Alimentos había que
    // escribirlo tres veces (barra, cajón y barra de abajo). Ahora las tres preguntan aquí.
    const BADGE_TESTID = {
        '/admin/leads': 'new-leads-badge',
        '/admin/messages': 'unread-messages-badge',
        '/admin/alimentos': 'sugerencias-pendientes-badge',
    };
    const badgeFor = (item) => {
        if (item.disabled) return 0;
        if (item.path === '/admin/leads') return newLeadsCount;
        if (item.path === '/admin/messages') return unreadMessages;
        if (item.path === '/admin/alimentos') return sugerenciasPendientes;
        return 0;
    };

    const handleLogout = () => {
        logout();
        navigate('/auth');
        toast.success('Sesión cerrada');
    };

    return (
        <div className="min-h-screen bg-[#0A0A0A] flex">
            {/* Sidebar (desktop) - misma estructura y estilo que el sidebar del modo cliente */}
            <aside className="w-64 bg-[#0A0A0A] border-r border-white/10 h-screen sticky top-0 hidden lg:flex flex-col flex-shrink-0">
                <div className="flex items-center justify-between h-16 px-4 border-b border-white/10">
                    <JG12Logo size="sm" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#FF671F] bg-[#FF671F]/10 border border-[#FF671F]/25 rounded-md px-2 py-0.5" data-testid="etiqueta-rol">{rolDelEquipo(user)}</span>
                </div>

                <nav className="flex-1 overflow-y-auto no-scrollbar p-3 space-y-1">
                    {navItems.map((item) => (
                        <EntradaDelMenu
                            key={item.path}
                            item={item}
                            className={`relative flex items-center gap-3 rounded-xl px-3.5 py-2.5 transition-all ${
                                isActive(item.path, item.exact)
                                    ? 'bg-[#FF671F] text-white font-semibold'
                                    : 'text-white/60 hover:text-white hover:bg-white/[0.07]'
                            }`}
                        >
                            <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={2} />
                            <span className="text-sm">{item.label}</span>
                            {badgeFor(item) > 0 && (
                                <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
                                    data-testid={BADGE_TESTID[item.path]}
                                    title={item.path === '/admin/alimentos'
                                        ? `${badgeFor(item)} ${badgeFor(item) === 1 ? 'sugerencia de alimento pendiente' : 'sugerencias de alimento pendientes'} de revisar`
                                        : undefined}>
                                    {badgeFor(item) > 99 ? '99+' : badgeFor(item)}
                                </span>
                            )}
                        </EntradaDelMenu>
                    ))}
                </nav>

                <div className="p-3 border-t border-white/10 space-y-2">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#FF671F]/15 rounded-xl flex items-center justify-center flex-shrink-0">
                            <span className="text-[#FF671F] font-bold font-heading text-lg">{user?.name?.charAt(0)?.toUpperCase()}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-semibold text-white text-sm truncate">{user?.name}</p>
                            <Badge className="bg-[#FF671F]/20 text-[#FF671F] border-0 text-xs uppercase">{rolDelEquipo(user)}</Badge>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate('/dashboard')}
                        data-testid="use-app-btn"
                        className="flex items-center gap-2 w-full rounded-lg px-3 py-2.5 text-white/50 hover:text-[#FF671F] hover:bg-[#FF671F]/10 transition-colors"
                    >
                        <Utensils className="w-4 h-4" /> <span className="text-sm">Usar app (modo cliente)</span>
                    </button>
                    <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 w-full rounded-lg px-3 py-2.5 text-white/50 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    >
                        <LogOut className="w-4 h-4" /> <span className="text-sm">Cerrar sesión</span>
                    </button>
                </div>
            </aside>

            {/* Main Content: scrollea la página entera (como en modo cliente),
                con barra superior móvil de hamburguesa */}
            <div className="flex-1 min-w-0 flex flex-col">
                <header className="lg:hidden sticky top-0 z-40 bg-[#0A0A0A] border-b border-white/10 h-14 flex items-center justify-between px-4">
                    <button onClick={() => setDrawerOpen(true)} data-testid="admin-mobile-menu-btn"
                        className="w-10 h-10 -ml-2 rounded-lg flex items-center justify-center text-white/80 hover:bg-white/10">
                        <Menu className="w-6 h-6" />
                    </button>
                    <JG12Logo size="sm" />
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-[#FF671F] bg-[#FF671F]/10 border border-[#FF671F]/25 rounded-md px-2 py-0.5" data-testid="etiqueta-rol">{rolDelEquipo(user)}</span>
                        {/* LA CAMPANA, TAMBIÉN CON LA VENTANA ESTRECHA (I6, 12-10): solo estaba
                            en la portada, y por debajo de 1.024 px no se encontraba. Aquí está
                            en todas las pantallas del panel. */}
                        <CampanaDelEquipo campana={campana} testId="notif-bell-cabecera" />
                    </div>
                </header>
                <main className="flex-1 min-w-0 pb-20 lg:pb-0">
                    {/* Si una pantalla revienta, que se caiga ELLA y no la app entera: el
                        menú y el resto de secciones siguen accesibles. Ver LimiteDeError. */}
                    <LimiteDeError clave={location.pathname + location.search}>
                        <Outlet context={{ campana }} />
                    </LimiteDeError>
                </main>
            </div>

            {/* Mobile bottom nav: 4 accesos principales + "Más" (drawer), como en modo cliente */}
            <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#0A0A0A] border-t border-white/10 barra-fija-abajo" data-testid="admin-mobile-nav">
                <div className="flex items-stretch h-16">
                    {bottomItems.map((item) => {
                        const active = isActive(item.path, item.exact);
                        const badge = badgeFor(item);
                        return (
                            <EntradaDelMenu key={item.path} item={item}
                                className={`relative flex flex-col items-center justify-center flex-1 gap-1 transition-colors ${active ? 'text-[#FF671F]' : 'text-white/55'}`}
                            >
                                <span className="relative">
                                    <item.icon className="w-[22px] h-[22px]" strokeWidth={active ? 2.5 : 2} />
                                    {badge > 0 && (
                                        <span className="absolute -top-1.5 -right-2 min-w-4 h-4 px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                                            {badge > 99 ? '99+' : badge}
                                        </span>
                                    )}
                                </span>
                                <span className={`text-[10px] ${active ? 'font-bold' : 'font-medium'}`}>{item.label}</span>
                            </EntradaDelMenu>
                        );
                    })}
                    <button onClick={() => setDrawerOpen(true)} data-testid="admin-bottomnav-mas"
                        className="flex flex-col items-center justify-center flex-1 gap-1 text-white/55">
                        <Menu className="w-[22px] h-[22px]" strokeWidth={2} />
                        <span className="text-[10px] font-medium">Más</span>
                    </button>
                </div>
            </nav>

            {/* Mobile drawer (hamburguesa), como en modo cliente */}
            {drawerOpen && (
                <div className="lg:hidden fixed inset-0 z-[60]" data-testid="admin-mobile-drawer">
                    <div className="absolute inset-0 bg-black/50 animate-fade-in" onClick={() => setDrawerOpen(false)} />
                    <div className="absolute inset-y-0 left-0 w-[82%] max-w-xs bg-[#0A0A0A] flex flex-col animate-slide-up">
                        <div className="flex items-center justify-between h-14 px-4 border-b border-white/10">
                            <JG12Logo size="sm" />
                            <button onClick={() => setDrawerOpen(false)} data-testid="admin-drawer-close"
                                className="w-9 h-9 rounded-lg flex items-center justify-center text-white/60 hover:bg-white/10">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <nav className="flex-1 overflow-y-auto no-scrollbar p-3 space-y-1">
                            {navItems.map((item) => {
                                const active = isActive(item.path, item.exact);
                                const badge = badgeFor(item);
                                return (
                                    <EntradaDelMenu key={item.path} item={item} onClick={() => setDrawerOpen(false)}
                                        className={`relative flex items-center gap-3 rounded-xl px-3.5 py-2.5 transition-all ${active ? 'bg-[#FF671F] text-white font-semibold' : 'text-white/60 hover:text-white hover:bg-white/[0.07]'}`}
                                    >
                                        <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={2} />
                                        <span className="text-sm">{item.label}</span>
                                        {badge > 0 && (
                                            <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                                                {badge > 99 ? '99+' : badge}
                                            </span>
                                        )}
                                    </EntradaDelMenu>
                                );
                            })}
                        </nav>
                        <div className="p-3 border-t border-white/10 space-y-2">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-[#FF671F]/15 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <span className="text-[#FF671F] font-bold font-heading text-lg">{user?.name?.charAt(0)?.toUpperCase()}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-white text-sm truncate">{user?.name}</p>
                                    <Badge className="bg-[#FF671F]/20 text-[#FF671F] border-0 text-xs uppercase">{rolDelEquipo(user)}</Badge>
                                </div>
                            </div>
                            <button onClick={() => navigate('/dashboard')}
                                className="flex items-center gap-2 w-full rounded-lg px-3 py-2.5 text-white/50 hover:text-[#FF671F] hover:bg-[#FF671F]/10 transition-colors">
                                <Utensils className="w-4 h-4" /> <span className="text-sm">Usar app (modo cliente)</span>
                            </button>
                            <button onClick={handleLogout}
                                className="flex items-center gap-2 w-full rounded-lg px-3 py-2.5 text-white/50 hover:text-red-400 hover:bg-red-500/10 transition-colors">
                                <LogOut className="w-4 h-4" /> <span className="text-sm">Cerrar sesión</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export { AdminDashboard, AdminLayout };
